import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <Loader2 className="animate-spin text-slate-500" size={28} />
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const ADMIN_ROLES = ["admin", "super_admin", "compliance_director", "senior_verification_manager"];
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

export function SeniorRoute({ children }) {
  const { user, loading } = useAuth();
  const SENIOR_ROLES = ["super_admin", "compliance_director"];
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!SENIOR_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Capability-based guard: aligns direct-URL access to the same canSee* capability
// helpers the sidebar uses, so a route can't be reached by URL when its nav is hidden
// (least privilege). `can` is a role→bool predicate from lib/roles.js.
export function CapabilityRoute({ can, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Legal/compliance reviewer roles (mirrors backend LEGAL_SIGNOFF_ROLES).
export const LEGAL_REVIEWER_ROLES = ["super_admin", "compliance_director", "senior_verification_manager"];
export function LegalRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!LEGAL_REVIEWER_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Centralized senior technical role set (add future roles here only).
export const INTEL_ROLES = ["super_admin", "compliance_director", "security_admin"];
export function IntelRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!INTEL_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}
